/**
 * Audit Summary Component
 * 
 * Displays a summary card with key audit information
 */

import type { AuditSummary as AuditSummaryType } from '@/types/audit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface AuditSummaryProps {
  summary: AuditSummaryType;
}

export function AuditSummary({ summary }: AuditSummaryProps) {
  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return formatDistanceToNow(date, { addSuffix: true });
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Summary
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {summary.createdAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span className="font-medium">
              {formatDate(summary.createdAt)} by {summary.createdBy}
            </span>
          </div>
        )}
        
        {summary.lastModifiedAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Last Modified:</span>
            <span className="font-medium">
              {formatDate(summary.lastModifiedAt)} by {summary.lastModifiedBy}
            </span>
          </div>
        )}
        
        {summary.submittedAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Submitted:</span>
            <span className="font-medium">{formatDate(summary.submittedAt)}</span>
          </div>
        )}
        
        {summary.approvedAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Approved:</span>
            <span className="font-medium text-green-600">
              {formatDate(summary.approvedAt)} by {summary.approvedBy}
            </span>
          </div>
        )}
        
        {summary.rejectedAt && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Rejected:</span>
            <span className="font-medium text-red-600">
              {formatDate(summary.rejectedAt)} by {summary.rejectedBy}
            </span>
          </div>
        )}
        
        <div className="flex justify-between pt-2 border-t">
          <span className="text-muted-foreground">Total Changes:</span>
          <span className="font-semibold">{summary.totalChanges}</span>
        </div>
      </CardContent>
    </Card>
  );
}
